/**
 * advance-construction.ts — per-tick Constructor build pass (M14).
 *
 * Runs after advanceShipMovement / advanceShipJumps / advancePendingJumps
 * so a Constructor that started moving this tick has `constructionOrder`
 * already cleared by the command path before we inspect it here. (The
 * command appliers clear constructionOrder whenever a new move, jump, or
 * cloak order lands on a ship, matching STRUCTURES §3 "Constructor stays
 * on site" semantics.)
 *
 * Per Constructor (ascending ship id for determinism):
 *   1. Skip if constructionOrder is null.
 *   2. Skip defensively if the Constructor is mid-jump, cloaked, or
 *      EMP-disabled — the command applier shouldn't allow those states to
 *      coexist, but if some other pass flipped the ship into one, freeze
 *      the construction (don't tick) rather than mutate ambiguously.
 *   3. Decrement ticksRemaining. On reaching 0, spawn the structure as a
 *      new ShipInstance at the Constructor's current (sx, sy) — full
 *      hull/shields/energy, weaponSlots initialised from the template.
 *   4. Clear constructionOrder on completion.
 */

import { initWeaponSlots } from './scenario';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import {
  MINEFIELD_CHASSIS,
  mineCountInitial,
} from './balance-space-structures';
import {
  allocEntityId,
  type Empire,
  type GameState,
  type ShipInstance,
} from '../state/types';

/** Advance space-structure construction by one tick. Mutates state. */
export function advanceConstruction(state: GameState): void {
  const constructors = [...state.ships.values()].sort((a, b) => a.id - b.id);
  for (const ship of constructors) {
    if (ship.constructionOrder === null) continue;
    if (ship.systemId === null) continue;      // mid-jump (defensive)
    if (ship.cloaked) continue;                 // defensive
    if (ship.disabledTicks > 0) continue;       // EMP'd — freeze build
    if (ship.currentHull <= 0) continue;        // dead — will be despawned
    // Don't tick while traveling to the build site. advance-shipMovement
    // clears moveOrder on arrival; after that, construction begins.
    if (ship.moveOrder !== null) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;

    ship.constructionOrder.ticksRemaining -= 1;
    if (ship.constructionOrder.ticksRemaining <= 0) {
      const order = ship.constructionOrder;
      spawnBuiltStructure(state, empire, ship, order.structureKind, order.sx, order.sy);
      ship.constructionOrder = null;
    }
  }
}

/**
 * Spawn a completed space structure at the Constructor's current (sx, sy).
 * Looks up the synthetic template from `state.shipTemplates` (injected by
 * the loader's `injectSpaceStructureTemplates`). Defensively no-ops if the
 * template isn't present — validation at command queue time should have
 * caught it, but keep the advance pass failure-tolerant.
 */
function spawnBuiltStructure(
  state: GameState,
  empire: Empire,
  constructor: ShipInstance,
  structureKind: string,
  sx: number,
  sy: number,
): void {
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${structureKind}`);
  if (!tmpl) return;
  if (constructor.systemId === null) return;

  const structure: ShipInstance = {
    id: allocEntityId(state),
    chassis: structureKind,
    ownerEmpireId: empire.id,
    currentHull: effectiveMaxHull(state, empire, tmpl),
    currentShields: effectiveMaxShields(state, empire, tmpl),
    currentEnergy: tmpl.energyCapacity ?? 0,
    disabledTicks: 0,
    cloaked: false,
    systemId: constructor.systemId,
    sx,
    sy,
    moveOrder: null,
    jumpOriginSystemId: null,
    jumpTargetSystemId: null,
    jumpTargetJumpPointId: null,
    jumpTargetGateId: null,
    pendingWarpGateLinkId: null,
    jumpTicksRemaining: 0,
    jumpDurationTotal: 0,
    lastFiredTick: null,
    lastFiredTargetShipId: null,
    pendingJumpPointId: null,
    pendingColoniseTargetId: null,
    constructionOrder: null,
    bombardOrder: null,
    weaponSlots: initWeaponSlots(
      tmpl.weaponLoadout,
      state.weapons,
      empire.researchedTechs,
    ),
    // v3M22.1 — ship-commands defaults; space structures are 'idle'
    // and stationary (specialAbilities includes STATIONARY_SPECIAL_ABILITY),
    // so they're never the subject of patrol/explore/guard commands.
    commandKind: 'idle',
    waypoints: [],
    guardTargetKind: null,
    guardTargetId: null,
  };
  // v3M2.3 — initialise Minefield-specific runtime fields on spawn.
  // Non-Minefield space structures leave these as `undefined`.
  if (structureKind === MINEFIELD_CHASSIS) {
    structure.mineCount = mineCountInitial(state.structuresData);
    structure.mineCloakRevealed = false;
    structure.mineLastTriggerTickByShipId = [];
  }
  state.ships.set(structure.id, structure);
  empire.shipIds.push(structure.id);
}
